import * as React from 'react';
import { MouseEvent, useContext, useLayoutEffect, useRef, useState } from 'react';
import styled, { keyframes, ThemeContext } from 'styled-components';
import { DataValue, inherentInPortTemplate, inherentOutPortTemplate, NodeInstanceDataPort } from '@glueapp/graph-types';
import { getColorFromDataType } from '../../helpers/getColorFromDataType';
import { usePortPositions } from '@/GraphEditor/drawingLayer/contexts/PortPositionsContext';
import { usePortEvents } from '@/GraphEditor/drawingLayer/contexts/PortEventsContext';
import { useNodeExecutionResult } from '@/GraphEditor/drawingLayer/contexts/NodeExecutionResultContext';
import { ThemeType } from '@/theme/glueTheme';
import { usePortOverlayNode } from '@/GraphEditor/drawingLayer/contexts/PortOverlayNodeContext';
import { useCurrentNode } from '@/GraphEditor/drawingLayer/contexts/CurrentNodeContext';
import { useHoverIntent } from '@/GraphEditor/drawingLayer/typeDisplay/useHoverIntent';
import { TypeDisplayPopover } from '@/GraphEditor/drawingLayer/typeDisplay/TypeDisplayPopover';
import { canHaveUserDefinedType } from '@/GraphEditor/drawingLayer/typeDisplay/portUserTypeEligibility';
import { Settings } from 'lucide-react';

const Port = styled.li`
    position: relative;
    padding-left: 10px;
    padding-right: 10px;
    padding-top: 6px;
    padding-bottom: 6px;
`;

const InherentPort = styled.div`
    position: relative;
    width: 4px;
`;

const InPortTypeAnchor = styled.div`
    position: absolute;
    left: 0;
    top: 50%;
`;

export const OutPortTypeAnchor = styled.div`
    position: absolute;
    right: 0;
    top: 50%;
`;

const FadeIn = keyframes`
    from {opacity: 0;}
    to { opacity: 1 }
`;

const PortTypeDisplay = styled.div`
    position: absolute;
    top: 0;
    background-color: ${p => p.theme.backgroundColor};
    color: ${p => p.theme.primaryContrastColor};
    white-space: nowrap;
    padding: 4px;
    pointer-events: none;
    transform: translateY(-50%);
    border-radius: 8px;
    opacity: 0;
    border: 2px solid ${props => props.theme.separatorColor};
    animation: ${FadeIn} 0.2s ease forwards;
    animation-delay: 0.3s;
`;

const InPortTypeDisplay = styled(PortTypeDisplay)`
    right: 16px;
`;

const OutPortTypeDisplay = styled(PortTypeDisplay)`
    left: 16px;
`;

export interface PortCircleProps {
    color: string;
}

export const InPortCircle = styled.div<PortCircleProps>`
    width: 13px;
    height: 13px;
    border-radius: 50%;
    text-align: left;
    background-color: ${props => props.color};
    border: 1px solid #3a3d42;
    &:hover {
        transform: translateY(-50%) scale(1.25);
    }
    position: absolute;
    left: -7px;
    top: 50%;
    transform: translateY(-50%);
`;

export const OutPortCircle = styled.div<PortCircleProps>`
    width: 13px;
    height: 13px;
    border-radius: 50%;
    background-color: ${props => props.color};
    border: 1px solid #3a3d42;
    &:hover {
        transform: translateY(-50%) scale(1.25);
    }
    position: absolute;
    right: -7px;
    top: 50%;
    transform: translateY(-50%);
`;

export interface InPortProps {
    port: NodeInstanceDataPort;
}

const TypeEditGearButton = styled.button`
    background: transparent;
    border: 0;
    padding: 0;
    margin: 0 4px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    opacity: 0.55;
    color: ${props => props.theme.primaryContrastColor};
    &:hover {
        opacity: 1;
    }
`;

export const InPort: React.FC<InPortProps> = ({ port }) => {
    const iconElementRef = useRef<HTMLDivElement | null>(null);
    const { setInPortPosition } = usePortPositions();
    const { onInPortMouseUp, onInPortMouseDown } = usePortEvents();
    const { isVisible, triggerProps, popoverProps } = useHoverIntent({ showDelay: 300, hideDelay: 150 });
    const { openOverlay } = usePortOverlayNode();
    const node = useCurrentNode();
    const [typeEditOpen, setTypeEditOpen] = useState<boolean>(false);
    const theme = useContext<ThemeType>(ThemeContext);

    useLayoutEffect(() => {
        const element = iconElementRef.current;
        if (element) {
            const boundingRect = element.getBoundingClientRect();
            setInPortPosition(port.name, {
                x: boundingRect.x + boundingRect.width / 2,
                y: boundingRect.y + boundingRect.height / 2,
            });
        }
    });

    const handlePortNameClick = (e: React.PointerEvent) => {
        e.stopPropagation();
        e.preventDefault();
        openOverlay(port.name, 'in');
    };

    const declaredPort = node.inPorts.find(p => p.name === port.name);
    const canEditType = declaredPort ? canHaveUserDefinedType(declaredPort.type) : false;
    const popoverVisible = typeEditOpen || isVisible;
    const popoverMode: 'display' | 'edit' = typeEditOpen ? 'edit' : 'display';

    const handleGearClick = (e: React.PointerEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setTypeEditOpen(open => !open);
    };

    return (
        <Port {...triggerProps}>
            <span onPointerDown={handlePortNameClick} style={{ cursor: 'pointer' }} className="hover:underline">
                {port.name}
            </span>
            {canEditType ? (
                <TypeEditGearButton
                    type="button"
                    aria-label={`Edit type for ${port.name}`}
                    data-type-edit-trigger="true"
                    onPointerDown={handleGearClick}
                >
                    <Settings size={11} />
                </TypeEditGearButton>
            ) : null}
            <InPortCircle
                ref={iconElementRef}
                color={getColorFromDataType(port.type, theme)}
                aria-label={`Input ${port.name}`}
                onPointerDown={(e: MouseEvent) => {
                    e.stopPropagation();
                    onInPortMouseDown(e, port.name);
                }}
                onPointerUp={(e: MouseEvent) => {
                    e.stopPropagation();
                    onInPortMouseUp(e, port.name);
                }}
            />
            <InPortTypeAnchor>
                <TypeDisplayPopover
                    port={port}
                    side="in"
                    visible={popoverVisible}
                    popoverHoverProps={popoverProps}
                    mode={popoverMode}
                    onRequestClose={typeEditOpen ? () => setTypeEditOpen(false) : undefined}
                    onRequestEdit={canEditType ? () => setTypeEditOpen(true) : undefined}
                />
            </InPortTypeAnchor>
        </Port>
    );
};

export interface OutPortProps {
    port: NodeInstanceDataPort;
    outputValue?: DataValue;
    executionDecision?: boolean;
}

const OutputIndicator = styled.button<{ $color: string; $isOpen?: boolean }>`
    position: absolute;
    left: 12px;
    top: 0;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => props.$color};
    border: 2px solid ${props => (props.$isOpen ? props.theme.primaryContrastColor : props.theme.separatorColor)};
    cursor: pointer;
    pointer-events: all;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    outline: none;
    transition:
        transform 0.1s ease,
        border-color 0.15s ease;

    &:hover {
        transform: translateY(-50%) scale(1.2);
    }
`;

const OutputPreview = styled.div`
    position: absolute;
    left: 32px;
    top: 0;
    transform: translateY(-50%);
    pointer-events: none;
    max-width: 260px;
    max-height: 80px;
    overflow: hidden;
    white-space: pre;
    text-align: left;
    text-overflow: ellipsis;
    padding: 4px 8px;
    border-radius: 8px;
    font-size: 11px;
    font-family: monospace;
    opacity: 0.7;
    background: ${props => props.theme.backgroundColor};
    color: ${props => props.theme.textColor};
    border: 1px solid ${props => props.theme.separatorColor};
`;

function formatPreviewValue(value: unknown): string {
    if (value === null || value === undefined) return 'null';
    switch (typeof value) {
        case 'string':
            return `"${value}"`;
        case 'number':
        case 'bigint':
        case 'boolean':
            return String(value);
        case 'object':
            return JSON.stringify(value, null, 2);
        default:
            return String(value);
    }
}

export const OutPort: React.FC<OutPortProps> = ({ port, outputValue, executionDecision }) => {
    const iconElementRef = useRef<HTMLDivElement | null>(null);
    const { setOutPortPosition } = usePortPositions();
    const { onOutPortMouseDown } = usePortEvents();
    const { isVisible, triggerProps, popoverProps } = useHoverIntent({ showDelay: 300, hideDelay: 150 });
    const [indicatorHovered, setIndicatorHovered] = useState<boolean>(false);
    const [typeEditOpen, setTypeEditOpen] = useState<boolean>(false);
    const theme = useContext<ThemeType>(ThemeContext);
    const { openOverlay, closeOverlay, isActive } = usePortOverlayNode();
    const node = useCurrentNode();
    const isOverlayOpen = isActive(port.name, 'out');

    useLayoutEffect(() => {
        const element = iconElementRef.current;
        if (element) {
            const boundingRect = element.getBoundingClientRect();
            setOutPortPosition(port.name, {
                x: boundingRect.x + boundingRect.width / 2,
                y: boundingRect.y + boundingRect.height / 2,
            });
        }
    });

    const hasOutput = outputValue !== undefined || executionDecision !== undefined;

    const handleIndicatorPointerDown = (e: React.PointerEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (isOverlayOpen) {
            closeOverlay();
        } else {
            openOverlay(port.name, 'out');
        }
    };

    const declaredPort = node.outPorts.find(p => p.name === port.name);
    const canEditType = declaredPort ? canHaveUserDefinedType(declaredPort.type) : false;
    const popoverVisible = typeEditOpen || isVisible;
    const popoverMode: 'display' | 'edit' = typeEditOpen ? 'edit' : 'display';
    const handleGearClick = (e: React.PointerEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setTypeEditOpen(open => !open);
    };

    return (
        <Port {...triggerProps}>
            <OutPortCircle
                ref={iconElementRef}
                color={getColorFromDataType(port.type, theme)}
                aria-label={`Output ${port.name}`}
                onPointerDown={event => {
                    event.stopPropagation();
                    onOutPortMouseDown(event, port.name);
                }}
            />
            {port.name}
            {canEditType ? (
                <TypeEditGearButton
                    type="button"
                    aria-label={`Edit type for ${port.name}`}
                    data-type-edit-trigger="true"
                    onPointerDown={handleGearClick}
                >
                    <Settings size={11} />
                </TypeEditGearButton>
            ) : null}
            <OutPortTypeAnchor>
                {!hasOutput ? (
                    <TypeDisplayPopover
                        port={port}
                        side="out"
                        visible={popoverVisible}
                        popoverHoverProps={popoverProps}
                        mode={popoverMode}
                        onRequestClose={typeEditOpen ? () => setTypeEditOpen(false) : undefined}
                        onRequestEdit={canEditType ? () => setTypeEditOpen(true) : undefined}
                    />
                ) : null}
                {hasOutput ? (
                    <>
                        <OutputIndicator
                            $color={executionDecision === false ? '#9ca3af' : '#22c55e'}
                            $isOpen={isOverlayOpen}
                            onPointerDown={handleIndicatorPointerDown}
                            onMouseEnter={() => setIndicatorHovered(true)}
                            onMouseLeave={() => setIndicatorHovered(false)}
                            aria-label="View output"
                        >
                            <svg width="8" height="8" viewBox="0 0 8 8" fill="white">
                                <circle cx="4" cy="4" r="2" />
                            </svg>
                        </OutputIndicator>
                        {indicatorHovered && !isOverlayOpen && outputValue !== undefined ? (
                            <OutputPreview>{formatPreviewValue(outputValue)}</OutputPreview>
                        ) : null}
                    </>
                ) : null}
            </OutPortTypeAnchor>
        </Port>
    );
};

export interface ShowMorePortProps {
    onPointerDown?: (event: React.PointerEvent<HTMLDivElement>) => void;
}

export const ShowMorePortsOutPort: React.FC<ShowMorePortProps> = ({ onPointerDown }) => {
    return (
        <Port>
            <OutPortCircle
                color={'#3a3d42'}
                onPointerDown={event => {
                    event.stopPropagation();
                    if (onPointerDown) {
                        onPointerDown(event);
                    }
                }}
            />
            More...
        </Port>
    );
};

export const InherentInPort = () => {
    const node = useCurrentNode();
    const iconElementRef = useRef<HTMLDivElement | null>(null);
    const { setInPortPosition } = usePortPositions();
    const { onInPortMouseUp, onInPortMouseDown } = usePortEvents();
    const [hovered, setHovered] = useState<boolean>(false);
    const theme = useContext<ThemeType>(ThemeContext);

    useLayoutEffect(() => {
        const element = iconElementRef.current;
        if (element) {
            const boundingRect = element.getBoundingClientRect();
            setInPortPosition(inherentInPortTemplate.name, {
                x: boundingRect.x + boundingRect.width / 2,
                y: boundingRect.y + boundingRect.height / 2,
            });
        }
    });

    if (node.hideInherentInPort) return <InherentPort />;

    return (
        <InherentPort as="div" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
            <InPortCircle
                ref={iconElementRef}
                color={theme.primaryContrastColor}
                aria-label={`Input ${inherentInPortTemplate.name}`}
                onPointerDown={(e: MouseEvent) => {
                    e.stopPropagation();
                    onInPortMouseDown(e, inherentInPortTemplate.name, 'inherent');
                }}
                onPointerUp={(e: MouseEvent) => {
                    e.stopPropagation();
                    onInPortMouseUp(e, inherentInPortTemplate.name, 'inherent');
                }}
            />
            <InPortTypeAnchor>
                {hovered ? (
                    <InPortTypeDisplay>
                        The default In Port
                        <br />
                        You can plug anything into it to trigger this block
                        <br />
                        Takes in no data for the block
                    </InPortTypeDisplay>
                ) : null}
            </InPortTypeAnchor>
        </InherentPort>
    );
};

export const InherentOutPort = () => {
    const node = useCurrentNode();
    const iconElementRef = useRef<HTMLDivElement | null>(null);
    const { setOutPortPosition } = usePortPositions();
    const { onOutPortMouseDown } = usePortEvents();
    const [hovered, setHovered] = useState<boolean>(false);
    const theme = useContext<ThemeType>(ThemeContext);
    const executionResult = useNodeExecutionResult();
    const showOutput = executionResult !== undefined;
    const { openOverlay, closeOverlay, isActive } = usePortOverlayNode();
    const isOverlayOpen = isActive(inherentOutPortTemplate.name, 'out');

    useLayoutEffect(() => {
        const element = iconElementRef.current;
        if (element) {
            const boundingRect = element.getBoundingClientRect();
            setOutPortPosition(inherentOutPortTemplate.name, {
                x: boundingRect.x + boundingRect.width / 2,
                y: boundingRect.y + boundingRect.height / 2,
            });
        }
    });

    if (node.hideInherentOutPort) return <InherentPort />;

    return (
        <InherentPort as="div" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
            <OutPortCircle
                ref={iconElementRef}
                color={theme.primaryContrastColor}
                aria-label={`Output ${inherentOutPortTemplate.name}`}
                onPointerDown={event => {
                    event.stopPropagation();
                    onOutPortMouseDown(event, inherentOutPortTemplate.name, 'inherent');
                }}
            />
            <OutPortTypeAnchor>
                {hovered && !showOutput ? (
                    <OutPortTypeDisplay>
                        The default Out Port
                        <br />
                        You can connect it to any default In Port and trigger it
                        <br />
                        Outputs no data from the block
                    </OutPortTypeDisplay>
                ) : null}
                {showOutput ? (
                    <OutputIndicator
                        $color="#22c55e"
                        $isOpen={isOverlayOpen}
                        onPointerDown={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (isOverlayOpen) {
                                closeOverlay();
                            } else {
                                openOverlay(inherentOutPortTemplate.name, 'out');
                            }
                        }}
                        aria-label="View output"
                    >
                        <svg width="8" height="8" viewBox="0 0 8 8" fill="white">
                            <circle cx="4" cy="4" r="2" />
                        </svg>
                    </OutputIndicator>
                ) : null}
            </OutPortTypeAnchor>
        </InherentPort>
    );
};
